import {
  useTheme
} from "./chunk-JVDTBYWX.js";
import {
  chipDismissTriggerTheme,
  chipIconTheme,
  chipLabelTheme,
  chipTheme
} from "./chunk-332RUHED.js";

// src/components/chip.tsx
import * as React from "react";
import { twMerge } from "tailwind-merge";
import Ripple from "material-ripple-effects";
import { jsx } from "react/jsx-runtime";
var ChipContext = React.createContext({
  size: "md",
  color: "primary",
  variant: "solid",
  open: true,
  setOpen: () => {
  }
});
function ChipRootBase({
  as,
  size,
  color,
  variant,
  className,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  children,
  isPill,
  ...props
}, ref) {
  const Component = as ?? "div";
  const contextTheme = useTheme();
  const theme = contextTheme?.chip ?? chipTheme;
  const defaultProps = theme?.defaultProps;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(true);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  size ??= defaultProps?.size ?? "md";
  color ??= defaultProps?.color ?? "primary";
  variant ??= defaultProps?.variant ?? "solid";
  isPill ??= defaultProps?.isPill ?? true;
  const styles = twMerge(
    theme.baseStyle,
    theme["size"][size],
    theme["variant"][variant][color],
    className
  );
  const contextValue = React.useMemo(
    () => ({
      size,
      color,
      variant,
      open,
      setOpen
    }),
    [size, color, variant, open, setOpen]
  );
  return open ? /* @__PURE__ */ jsx(
    Component,
    {
      ...props,
      ref,
      "data-open": open,
      "data-shape": isPill ? "pill" : "default",
      className: styles,
      children: /* @__PURE__ */ jsx(ChipContext.Provider, { value: contextValue, children })
    }
  ) : null;
}
ChipRootBase.displayName = "MaterialTailwind.Chip";
var ChipRoot = React.forwardRef(ChipRootBase);
function ChipLabelRoot({ as, className, children, ...props }, ref) {
  const Component = as ?? "span";
  const contextTheme = useTheme();
  const { size } = React.useContext(ChipContext);
  const theme = contextTheme?.chipLabel ?? chipLabelTheme;
  const styles = twMerge(
    theme.baseStyle,
    theme["size"][size || "md"],
    className
  );
  return /* @__PURE__ */ jsx(Component, { ...props, ref, className: styles, children });
}
ChipLabelRoot.displayName = "MaterialTailwind.ChipLabel";
var ChipLabel = React.forwardRef(ChipLabelRoot);
function ChipIconRoot({ as, className, children, ...props }, ref) {
  const Component = as ?? "span";
  const contextTheme = useTheme();
  const { size } = React.useContext(ChipContext);
  const theme = contextTheme?.chipIcon ?? chipIconTheme;
  const styles = twMerge(
    theme.baseStyle,
    theme["size"][size || "md"],
    className
  );
  return /* @__PURE__ */ jsx(Component, { ...props, ref, className: styles, children });
}
ChipIconRoot.displayName = "MaterialTailwind.ChipIcon";
var ChipIcon = React.forwardRef(ChipIconRoot);
function ChipDismissTriggerRoot({ as, ripple, className, children, ...props }, ref) {
  const Component = as ?? "button";
  const contextTheme = useTheme();
  const { size, color, variant, setOpen } = React.useContext(ChipContext);
  const theme = contextTheme?.chipDismissTrigger ?? chipDismissTriggerTheme;
  const defaultProps = theme?.defaultProps;
  ripple ??= defaultProps?.ripple ?? true;
  const rippleEffect = ripple !== void 0 && new Ripple();
  const handleClick = (event) => {
    setOpen?.(false);
    props.onClick?.(event);
    const isDarkRipple = variant === "ghost" || variant === "outline" || color === "secondary" || color === "warning";
    if (ripple) {
      rippleEffect.create(event, isDarkRipple ? "dark" : "light");
    }
  };
  const styles = twMerge(
    theme.baseStyle,
    theme["size"][size || "md"],
    className
  );
  return /* @__PURE__ */ jsx(Component, { ...props, ref, className: styles, onClick: handleClick, children: children || /* @__PURE__ */ jsx(
    "svg",
    {
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      color: "currentColor",
      className: "h-full w-full",
      children: /* @__PURE__ */ jsx(
        "path",
        {
          d: "M6.75827 17.2426L12.0009 12M17.2435 6.75736L12.0009 12M12.0009 12L6.75827 6.75736M12.0009 12L17.2435 17.2426",
          stroke: "currentColor",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      )
    }
  ) });
}
ChipDismissTriggerRoot.displayName = "MaterialTailwind.ChipDismissTrigger";
var ChipDismissTrigger = React.forwardRef(ChipDismissTriggerRoot);
var Chip = Object.assign(ChipRoot, {
  Icon: ChipIcon,
  Label: ChipLabel,
  DismissTrigger: ChipDismissTrigger
});
var chip_default = Chip;

export {
  ChipContext,
  ChipRoot,
  ChipLabel,
  ChipIcon,
  ChipDismissTrigger,
  Chip,
  chip_default
};
